<script setup lang="ts">
import type { NuxtUISize } from '~/types/elementsUi'
import { rootSvgImages } from '~/models/images'
import { nuxtUiMerge } from '~/utils/tw-utils'
import type { ModalProps } from '#ui/components/Modal.vue'
import type { FAIconName } from '#shared/types/generated-icons'
import type { ImageProps } from '~/types/images'
import type { ExtendedTailwindColor } from '#shared/types/elementsUi'

type VModalEmits = {
  open: []
  close: [e?: Event]
  cancel: []
}

type ExtendedModalUIProps = ModalProps['ui'] & {
  headerDescription?: string
}

interface VModalProps {
  title?: string
  subtitle?: string
  description?: string

  cancelLabel?: string

  indicatorText?: string
  indicatorCenter?: boolean
  indicatorIcon?: FAIconName

  noHeaderImage?: boolean
  headerImage?: ImageProps
  headerDarkImage?: ImageProps
  headerIcon?: FAIconName
  headerIconColor?: ExtendedTailwindColor

  size?: NuxtUISize

  ui?: ExtendedModalUIProps
}

const emits = defineEmits<VModalEmits>()

const props = withDefaults(defineProps<VModalProps>(), {
  title: undefined,
  description: undefined,

  cancelLabel: undefined,

  indicatorText: undefined,
  indicatorIcon: 'fa-arrow-right',

  headerImage: () => rootSvgImages['thuisgekookt-succes'],
  headerDarkImage: () => rootSvgImages['thuisgekookt-succes-dark'],
  headerIcon: undefined,
  headerIconColor: undefined,

  size: 'lg',
  ui: undefined
})

const isOpen = defineModel<boolean>('open', {
  required: false,
  default: false
})

const { t, te, tm } = useI18n()

const sizeClass: Record<NuxtUISize, string> = {
  xs: tw`max-w-xs`,
  sm: tw`max-w-sm`,
  md: tw`max-w-md`,
  lg: tw`max-w-lg`,
  xl: tw`max-w-xl`,
  '2xl': tw`max-w-2xl`,
  '3xl': tw`max-w-3xl`,
  '4xl': tw`max-w-4xl`
}

const computedUI = computed<ModalProps['ui']>(() =>
  nuxtUiMerge(props.ui, {
    content: tw`${sizeClass[props.size]} border-muted ring-0! border`,
    header: tw`border-b-0 flex flex-col min-h-auto relative`,
    body: tw`border-b-0`,
    close: tw`cursor-pointer p-0`,
    description: tw`text-base`
  })
)

const cancelLabel = computed(() =>
  props.cancelLabel && te(props.cancelLabel) ? t(props.cancelLabel) : t('buttons.cancel')
)

const headerDescription = computed(() => {
  if (!props.description) {
    return undefined
  }

  if (Array.isArray(tm(props.description))) {
    return tm(props.description)
  } else {
    return te(props.description) ? [t(props.description)] : [props.description]
  }
})

const openModal = () => {
  isOpen.value = true
  emits('open')
}

const closeModal = (e?: Event) => {
  isOpen.value = false
  emits('close', e)
}

const cancelModal = () => {
  closeModal()
  emits('cancel')
}

defineExpose({ openModal, closeModal })
</script>

<template>
  <UModal v-model:open="isOpen" :ui="computedUI" @close="cancelModal">
    <slot :open="openModal" name="open-modal">
      <VHtmlButton
        v-if="indicatorText"
        :class="[
          'text-highlight-green hover:text-secondary-green focus:text-secondary-green flex cursor-pointer justify-center',
          indicatorCenter && 'justify-center'
        ]"
        @click="openModal"
      >
        <span class="font-semibold">{{ t(indicatorText) }}</span>
        <VIcon :icon="indicatorIcon" />
      </VHtmlButton>
    </slot>

    <template #title>
      <div class="flex flex-col items-center">
        <template v-if="!noHeaderImage">
          <VIcon
            v-if="headerIcon"
            :icon="headerIcon"
            :color="headerIconColor"
            size="large"
            font-awesome-icon-size="4x"
            class="my-4"
          />
          <VImage
            v-else-if="headerImage"
            :image="headerImage"
            :dark="headerDarkImage"
            class="max-h-16"
            sizes="100px"
          />
        </template>
        <h1 v-if="title" class="mt-6 title text-center text-title">
          {{ te(title) ? t(title) : title }}
        </h1>
        <h2 v-if="subtitle" class="text-center text-muted">
          {{ te(subtitle) ? t(subtitle) : subtitle }}
        </h2>
      </div>
    </template>

    <template #description>
      <p
        v-for="(description, descriptionIndex) in headerDescription"
        :key="descriptionIndex"
        class="mt-4 text-default dark:text-white"
        :class="ui?.headerDescription"
      >
        {{ description }}
      </p>
    </template>

    <template v-if="$slots['content']" #body>
      <slot name="content" />
    </template>

    <template #footer>
      <footer class="flex w-full flex-col justify-center gap-2">
        <slot name="actions" />
        <slot :close="cancelModal" name="cancel">
          <VButton dark-mode-color="white" @click="cancelModal">
            {{ cancelLabel }}
          </VButton>
        </slot>
      </footer>
    </template>
  </UModal>
</template>

<style scoped></style>
